import type { ReactNode } from 'react';
import {
  Plus,
  Settings2,
  MoreVertical,
  Share2,
  Trash2,
  Globe,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MiniMonth } from './MiniMonth';
import type {
  CalendarCollection,
  CalendarSet,
  SharedCalendarSummary,
  CalendarSubscriptionDetail,
} from '../types';

interface CalendarSidebarProps {
  currentDate: Date;
  selectedDate: Date;
  weekStartDay: number;
  calendars: CalendarCollection[];
  onSelectDate: (date: Date) => void;
  onToggleCalendar: (id: string) => void;
  onAddCalendar: () => void;
  onDeleteCalendar?: (id: string) => Promise<void>;
  onShareCalendar?: (id: string) => void;
  sharedCalendars?: SharedCalendarSummary[];
  calendarSets?: CalendarSet[];
  activeCalendarSetId?: string | null;
  onActivateSet?: (id: string) => void;
  onDeactivateSet?: () => void;
  onManageSets?: () => void;
  subscriptions?: CalendarSubscriptionDetail[];
  onSubscribe?: () => void;
  onRefreshSubscription?: (id: string) => void;
  onUnsubscribe?: (id: string) => void;
  taskPanel?: ReactNode;
}

const NO_SET_VALUE = '__none__';

export function CalendarSidebar({
  currentDate,
  selectedDate,
  weekStartDay,
  calendars,
  onSelectDate,
  onToggleCalendar,
  onAddCalendar,
  onDeleteCalendar,
  onShareCalendar,
  sharedCalendars,
  calendarSets,
  activeCalendarSetId,
  onActivateSet,
  onDeactivateSet,
  onManageSets,
  subscriptions,
  onSubscribe,
  onRefreshSubscription,
  onUnsubscribe,
  taskPanel,
}: CalendarSidebarProps) {
  const showSets = calendarSets && calendarSets.length > 0;

  return (
    <div className="w-56 border-r flex flex-col gap-4 p-3 overflow-y-auto">
      {/* Mini month navigation */}
      <MiniMonth
        currentDate={currentDate}
        selectedDate={selectedDate}
        weekStartDay={weekStartDay}
        onSelectDate={onSelectDate}
      />

      {/* Calendar sets */}
      {(showSets || onManageSets) && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Sets
            </span>
            {onManageSets && (
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={onManageSets}
                title="Manage sets"
              >
                <Settings2 className="h-3 w-3" />
              </Button>
            )}
          </div>
          {showSets && (
            <Select
              value={activeCalendarSetId || NO_SET_VALUE}
              onValueChange={(v) => {
                if (v === NO_SET_VALUE) {
                  onDeactivateSet?.();
                } else {
                  onActivateSet?.(v);
                }
              }}
            >
              <SelectTrigger className="text-xs h-7">
                <SelectValue placeholder="No active set" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SET_VALUE}>No active set</SelectItem>
                {calendarSets!.map((set) => (
                  <SelectItem key={set.id} value={set.id}>
                    {set.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Calendar list */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Calendars
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={onAddCalendar}
            title="Add calendar"
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>

        <div className="space-y-1">
          {calendars.map((cal) => (
            <div
              key={cal.id}
              className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted group text-sm"
            >
              <label className="flex items-center gap-2 flex-1 cursor-pointer min-w-0">
                <Checkbox
                  checked={cal.isVisible}
                  onCheckedChange={() => onToggleCalendar(cal.id)}
                  className="h-3.5 w-3.5 flex-shrink-0"
                  style={
                    cal.isVisible
                      ? { backgroundColor: cal.color, borderColor: cal.color }
                      : { borderColor: cal.color }
                  }
                />
                <span className="truncate">{cal.name}</span>
              </label>
              {cal.eventCount !== undefined && (
                <span className="text-xs text-muted-foreground">{cal.eventCount}</span>
              )}
              {(onShareCalendar || onDeleteCalendar) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 opacity-0 group-hover:opacity-100"
                    >
                      <MoreVertical className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onShareCalendar && (
                      <DropdownMenuItem onClick={() => onShareCalendar(cal.id)}>
                        <Share2 className="h-3.5 w-3.5 mr-2" />
                        Share
                      </DropdownMenuItem>
                    )}
                    {onDeleteCalendar && !cal.isDefault && (
                      <DropdownMenuItem
                        onClick={() => onDeleteCalendar(cal.id)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>

        {/* Shared calendars */}
        {sharedCalendars && sharedCalendars.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
              Shared with me
            </div>
            <div className="space-y-1">
              {sharedCalendars.map((sc) => (
                <div
                  key={sc.id}
                  className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted text-sm"
                >
                  <Share2 className="h-3 w-3 flex-shrink-0" style={{ color: sc.calendarColor }} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{sc.calendarName}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      @{sc.ownerUsername}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Subscribed calendars */}
      {((subscriptions && subscriptions.length > 0) || onSubscribe) && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Subscriptions
            </span>
            {onSubscribe && (
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={onSubscribe}
                title="Subscribe to feed"
              >
                <Plus className="h-3 w-3" />
              </Button>
            )}
          </div>
          {subscriptions && subscriptions.length > 0 && (
            <div className="space-y-1">
              {subscriptions.map((sub) => (
                <div
                  key={sub.id}
                  className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted group text-sm"
                >
                  <Globe className="h-3 w-3 flex-shrink-0" style={{ color: sub.calendarColor }} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate flex items-center gap-1">
                      {sub.calendarName}
                      {sub.status === 'error' && (
                        <span title={sub.errorMessage || 'Sync error'}>
                          <AlertCircle className="h-3 w-3 text-destructive shrink-0" />
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground">{sub.eventCount} events</div>
                  </div>
                  {(onRefreshSubscription || onUnsubscribe) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 opacity-0 group-hover:opacity-100"
                        >
                          <MoreVertical className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {onRefreshSubscription && (
                          <DropdownMenuItem onClick={() => onRefreshSubscription(sub.id)}>
                            <RefreshCw className="h-3.5 w-3.5 mr-2" />
                            Refresh
                          </DropdownMenuItem>
                        )}
                        {onUnsubscribe && (
                          <DropdownMenuItem
                            onClick={() => onUnsubscribe(sub.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-2" />
                            Unsubscribe
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Task panel slot */}
      {taskPanel}
    </div>
  );
}
